import React, { useState, useRef, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { usePopper } from "react-popper";
import { FaChartLine } from "react-icons/fa";
import domtoimage from "dom-to-image";
import Button from "../../components/buttons/Button";
import { FaCopy, FaDownload, FaTimes } from "react-icons/fa";
import Notification from "../../components/Notifications/Notification";
import useNotification from "../../components/Notifications/useNotifications";
import { isCapacitor } from "../../utils/platform";
import { createLogger } from "../../services/Debug";
/* -----------------------------------------  -------------------------
   Data and Constants
------------------------------------------------------------------ */

const ANOMALIES = [
  "Hyperkinésie",
  "Normal",
  "Hypokinésie",
  "Akinésie",
  "Dyskinésie",
  "Anévrysme",
  "Non évaluable",
];

const ANOMALY_COLORS = {
  "Hyperkinésie": "#e0b0ff", // mauve
  "Normal": "#4ade80",
  "Hypokinésie": "#fbbf24",
  "Akinésie": "#ef4444",
  "Dyskinésie": "#a78bfa",
  "Anévrysme": "#6b7280",
  "Non évaluable": "#f3f4f6"
};

const segmentsDefinition = [
  // Basal (6 segments)
  { id: "basal_anterior", label: "BA", startAngle: -30, endAngle: 30, rIn: 115, rOut: 160 },
  { id: "basal_anterolateral", label: "BAL", startAngle: 30, endAngle: 90, rIn: 115, rOut: 160 },
  { id: "basal_inferolateral", label: "BIL", startAngle: 90, endAngle: 150, rIn: 115, rOut: 160 },
  { id: "basal_inferior", label: "BI", startAngle: 150, endAngle: 210, rIn: 115, rOut: 160 },
  { id: "basal_inferoseptal", label: "BIS", startAngle: 210, endAngle: 270, rIn: 115, rOut: 160 },
  { id: "basal_anteroseptal", label: "BAS", startAngle: 270, endAngle: 330, rIn: 115, rOut: 160 },
  // Mid (6 segments)
  { id: "mid_anterior", label: "MA", startAngle: -30, endAngle: 30, rIn: 70, rOut: 115 },
  { id: "mid_anterolateral", label: "MAL", startAngle: 30, endAngle: 90, rIn: 70, rOut: 115 },
  { id: "mid_inferolateral", label: "MIL", startAngle: 90, endAngle: 150, rIn: 70, rOut: 115 },
  { id: "mid_inferior", label: "MI", startAngle: 150, endAngle: 210, rIn: 70, rOut: 115 },
  { id: "mid_inferoseptal", label: "MIS", startAngle: 210, endAngle: 270, rIn: 70, rOut: 115 },
  { id: "mid_anteroseptal", label: "MAS", startAngle: 270, endAngle: 330, rIn: 70, rOut: 115 },
  // Apical (4 segments)
  { id: "apical_anterior", label: "AA", startAngle: -45, endAngle: 45, rIn: 25, rOut: 70 },
  { id: "apical_lateral", label: "AL", startAngle: 45, endAngle: 135, rIn: 25, rOut: 70 },
  { id: "apical_inferior", label: "AI", startAngle: 135, endAngle: 225, rIn: 25, rOut: 70 },
  { id: "apical_septal", label: "AS", startAngle: 225, endAngle: 315, rIn: 25, rOut: 70 },
  // Apex
  { id: "apex", label: "AP", rIn: 0, rOut: 25 },
];

const outerLabels = [
  { text: "Antérieur", angle: 0 },
  { text: "Antéro-latéral", angle: 60 },
  { text: "Inféro-latéral", angle: 120 },
  { text: "Inférieur", angle: 180 },
  { text: "Inféro-septal", angle: 240 },
  { text: "Antéro-septal", angle: 300 },
];

const SEGMENT_IDS = segmentsDefinition.map((s) => s.id);

/* ------------------------------------------------------------------
   Utility Functions
------------------------------------------------------------------ */

function getColorFromAnomaly(anomaly) {
  return ANOMALY_COLORS[anomaly] || ANOMALY_COLORS["Normal"];
}

function createAllNormalSegments() {
  const normalData = {};
  segmentsDefinition.forEach((seg) => {
    normalData[seg.id] = "Normal";
  });
  return normalData;
}

function parseInitialValue(value) {
  if (!value) return {};
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (e) {
      const log = createLogger('parseInitialValue');
      log.warn("Invalid JSON in value prop:", e);
      return {};
    }
  }
  return value;
}

function isEmpty(obj) {
  return !obj || Object.keys(obj).length === 0;
}

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
}

function arcPath(cx, cy, rIn, rOut, startAngle, endAngle) {
  const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;
  const p1 = polarToCartesian(cx, cy, rOut, startAngle);
  const p2 = polarToCartesian(cx, cy, rOut, endAngle);
  const p3 = polarToCartesian(cx, cy, rIn, endAngle);
  const p4 = polarToCartesian(cx, cy, rIn, startAngle);

  return [
    `M ${p1.x},${p1.y}`,
    `A ${rOut},${rOut} 0 ${largeArcFlag} 1 ${p2.x},${p2.y}`,
    `L ${p3.x},${p3.y}`,
    `A ${rIn},${rIn} 0 ${largeArcFlag} 0 ${p4.x},${p4.y}`,
    "Z",
  ].join(" ");
}

function simpleArcPath(cx, cy, r, startAngle, endAngle) {
  const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;
  const pStart = polarToCartesian(cx, cy, r, startAngle);
  const pEnd = polarToCartesian(cx, cy, r, endAngle);
  return [
    `M ${pStart.x},${pStart.y}`,
    `A ${r},${r} 0 ${largeArcFlag} 1 ${pEnd.x},${pEnd.y}`,
  ].join(" ");
}

function formatSegmentName(segmentId) {
  const [level, position] = segmentId.split('_');
  
  const levelMap = {
    'basal': 'basale',
    'mid': 'moyenne',
    'apical': 'apicale',
    'apex': 'apex'
  };

  const positionMap = {
    'anterior': 'antérieure',
    'anteroseptal': 'antéro-septale',
    'inferoseptal': 'inféro-septale',
    'inferior': 'inférieure',
    'inferolateral': 'inféro-latérale',
    'anterolateral': 'antéro-latérale',
    'septal': 'septale',
    'lateral': 'latérale'
  };

  if (segmentId === 'apex') {
    return "de l'apex";
  }

  return `de la paroi ${positionMap[position]} ${levelMap[level]}`;
}

/* ------------------------------------------------------------------
   BullseyeDiagram Component
------------------------------------------------------------------ */

function BullseyeDiagram({ className = "", value = {}, onChange, selectedAnomaly = "Normal", width = 500, height = 500, onSelectedAnomalyChange }) {
  const log = createLogger('BullseyeDiagram');
  const parsedValue = useMemo(() => {
    let parsed = parseInitialValue(value);
    if (isEmpty(parsed)) {
      parsed = createAllNormalSegments();
    }
    return parsed;
  }, [value]);

  // Mouse overlay tracking (visible when an anomaly other than Normal is selected)
  const containerRef = useRef(null);
  const [mouseOverlay, setMouseOverlay] = useState({ x: 0, y: 0, visible: false });
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, segmentId: null });

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    if (!selectedAnomaly || selectedAnomaly === "Normal") {
      if (mouseOverlay.visible) setMouseOverlay((prev) => ({ ...prev, visible: false }));
      return;
    }
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setMouseOverlay({ x, y, visible: true });
  };

  const handleMouseLeave = () => {
    if (mouseOverlay.visible) setMouseOverlay((prev) => ({ ...prev, visible: false }));
  };

  const NON_NORMAL_ANOMALIES = useMemo(() => ANOMALIES.filter(a => a !== "Normal"), []);

  const handleSegmentClick = (e, segmentId) => {
    e.stopPropagation();
    const newData = { ...parsedValue, [segmentId]: selectedAnomaly };
    onChange(newData);
  };

  const handleContextMenu = (e, segmentId) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = containerRef.current.getBoundingClientRect();
    setContextMenu({
      visible: true,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      segmentId: segmentId
    });
  };

  const handleAnomalySelect = (anomaly) => {
    if (contextMenu.segmentId) {
      const newData = { ...parsedValue, [contextMenu.segmentId]: anomaly };
      onChange(newData);
    }
    setContextMenu({ visible: false, x: 0, y: 0, segmentId: null });
  };

  // Close context menu when clicking outside
  React.useEffect(() => {
    const handleClick = () => {
      if (contextMenu.visible) {
        setContextMenu({ visible: false, x: 0, y: 0, segmentId: null });
      }
    };

    if (contextMenu.visible) {
      document.addEventListener('click', handleClick);
    }

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [contextMenu.visible]);

  const handleWheel = (e) => {
    if (!onSelectedAnomalyChange) return;
    // Prevent page scrolling when interacting with the diagram
    e.preventDefault();
    e.stopPropagation();
    const isDown = e.deltaY > 0;
    const list = NON_NORMAL_ANOMALIES;
    if (!Array.isArray(list) || list.length === 0) return;
    const current = selectedAnomaly || "Normal";
    let idx = list.indexOf(current);
    if (idx === -1) {
      // If currently Normal (or not found), start before first for down, or at first for up
      idx = isDown ? -1 : 0;
    }
    const nextIdx = (idx + (isDown ? 1 : -1) + list.length) % list.length;
    onSelectedAnomalyChange(list[nextIdx]);
  };

  const cx = 200;
  const cy = 200;
  const arcRadius = 180;
  const arcSpreadDegrees = 30;
  const halfSpread = arcSpreadDegrees / 2;

  return (
    <div style={{ width, margin: "0 auto" }}>
      <div
        ref={containerRef}
        style={{ position: "relative", display: "block", textAlign: "center" }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onWheel={handleWheel}
      >
        <svg
          className={className}
          viewBox="0 0 400 400"
          width={width}
          height={height}
          style={{ backgroundColor: "#fff" }}
        >
          {/* Segments */}
          {segmentsDefinition.map((seg) => {
            const anomaly = parsedValue[seg.id] || "Normal";
            const fillColor = getColorFromAnomaly(anomaly);

            if (seg.id === "apex") {
              return (
                <g key={seg.id}>
                  <circle
                    cx={cx}
                    cy={cy}
                    r={seg.rOut}
                    fill={fillColor}
                    stroke="#374151"
                    strokeWidth="2"
                    style={{ cursor: "pointer" }}
                    onClick={(e) => handleSegmentClick(e, seg.id)}
                    onContextMenu={(e) => handleContextMenu(e, seg.id)}
                  />
                  <text
                    x={cx}
                    y={cy}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="12"
                    fill="#000"
                    style={{ pointerEvents: "none", fontWeight: "bold" }}
                  >
                    {seg.label}
                  </text>
                </g>
              );
            }

            const d = arcPath(cx, cy, seg.rIn, seg.rOut, seg.startAngle, seg.endAngle);
            const midAngle = (seg.startAngle + seg.endAngle) / 2;
            const midRadius = (seg.rIn + seg.rOut) / 2;
            const { x, y } = polarToCartesian(cx, cy, midRadius, midAngle);

            return (
              <g key={seg.id}>
                <path
                  d={d}
                  fill={fillColor}
                  stroke="#374151"
                  strokeWidth="2"
                  style={{ cursor: "pointer" }}
                  onClick={(e) => handleSegmentClick(e, seg.id)}
                  onContextMenu={(e) => handleContextMenu(e, seg.id)}
                />
                <text
                  x={x}
                  y={y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="12"
                  fill="#000"
                  style={{ pointerEvents: "none", fontWeight: "bold" }}
                >
                  {seg.label}
                </text>
              </g>
            );
          })}

          {/* Labels extérieurs with curved text */}
          <defs>
            {outerLabels.map((item, idx) => {
              const startAngle = item.angle - halfSpread;
              const endAngle = item.angle + halfSpread;
              const d = simpleArcPath(cx, cy, arcRadius, startAngle, endAngle);
              return (
                <path
                  key={idx}
                  id={`outerLabelPath-${idx}`}
                  d={d}
                  fill="none"
                  stroke="none"
                />
              );
            })}
          </defs>

          {outerLabels.map((item, idx) => (
            <text key={idx} fontSize="12" fill="#000" fontWeight="bold">
              <textPath
                href={`#outerLabelPath-${idx}`}
                startOffset="50%"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {item.text}
              </textPath>
            </text>
          ))}
        </svg>

        {mouseOverlay.visible && selectedAnomaly && (
          <div
            style={{
              position: "absolute",
              left: mouseOverlay.x + 10,
              top: mouseOverlay.y - 20,
              background: "#111827",
              color: "#fff",
              padding: "4px 8px",
              borderRadius: 6,
              fontSize: 12,
              boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
              pointerEvents: "none",
              display: "flex",
              alignItems: "center",
              gap: 6,
              zIndex: 2
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: 10,
                height: 10,
                backgroundColor: getColorFromAnomaly(selectedAnomaly),
                borderRadius: 2,
                border: "1px solid rgba(255,255,255,0.5)"
              }}
            />
            {selectedAnomaly}
          </div>
        )}

        {contextMenu.visible && (
          <div
            style={{
              position: "absolute",
              left: contextMenu.x,
              top: contextMenu.y,
              background: "#fff",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              padding: "4px",
              boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
              zIndex: 1000,
              minWidth: 160
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ 
              fontSize: 11, 
              fontWeight: "bold", 
              padding: "6px 8px", 
              borderBottom: "1px solid #e5e7eb",
              color: "#374151"
            }}>
              Changer la cinétique
            </div>
            {ANOMALIES.map((anomaly) => (
              <div
                key={anomaly}
                onClick={() => handleAnomalySelect(anomaly)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 8px",
                  cursor: "pointer",
                  fontSize: 12,
                  transition: "background 0.2s",
                  borderRadius: 4
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f3f4f6"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
              >
                <div
                  style={{
                    width: 12,
                    height: 12,
                    backgroundColor: getColorFromAnomaly(anomaly),
                    border: "1px solid #d1d5db",
                    borderRadius: 2,
                    flexShrink: 0
                  }}
                />
                <span>{anomaly}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Légende */}
      {Object.values(parsedValue).some(v => v !== "Normal" && typeof v === 'string') && (
        <div
          style={{
            marginTop: 4,
            padding: "4px 6px",
            border: "1px solid #e5e7eb",
            borderRadius: 5,
            background: "#f9fafb",
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
          }}
        >
          {[...new Set(Object.values(parsedValue))].filter(v => v !== "Normal" && typeof v === 'string').map((anom) => (
            <div
              key={anom}
              style={{ display: "flex", alignItems: "center", gap: 3 }}
            >
              <div
                style={{
                  width: 9,
                  height: 9,
                  backgroundColor: getColorFromAnomaly(anom),
                  border: "1px solid #d1d5db",
                  borderRadius: 2,
                }}
              />
              <span style={{ fontSize: 8 }}>{anom}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------
   EchoReportView Component
------------------------------------------------------------------ */

function EchoReportView({ anchorEl, visible, onClose, structuredData = {}, onBullseyeChange, inline = false }) {
  const popperRef = useRef(null);
  const [selectedAnomaly, setSelectedAnomaly] = useState("Normal");
  const { notification, showNotification, hideNotification } = useNotification();
  const log = createLogger('EchoReportView');
  const { styles, attributes } = usePopper(
    inline ? null : anchorEl,
    inline ? null : popperRef.current,
    {
      placement: "auto",
      modifiers: [
        { name: "offset", options: { offset: [0, 10] } },
        { name: "preventOverflow", options: { padding: 8 } },
      ],
    }
  );

  // Handle Escape key to close
  React.useEffect(() => {
    if (inline) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && visible) {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, onClose, inline]);

  const applyToAll = (anomaly) => {
    const newData = {};
    SEGMENT_IDS.forEach((seg) => {
      newData[seg] = anomaly;
    });
    onBullseyeChange(newData);
  };

  const handleCopySVG = () => {
    const node = document.getElementById("bullseye-wrapper");
    if (!node) return;

    const scale = 2; // Render larger, then shrink for better quality
    const style = window.getComputedStyle(node);
    const width = parseInt(style.getPropertyValue('width'), 10);
    const height = parseInt(style.getPropertyValue('height'), 10);

    domtoimage
      .toPng(node, {
        width: width * scale,
        height: height * scale,
        style: {
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        },
      })
      .then((dataUrl) => {
        return fetch(dataUrl);
      })
      .then((res) => res.blob())
      .then((blob) => {
        const clipboardItem = new ClipboardItem({
          "image/png": blob,
        });
        return navigator.clipboard.write([clipboardItem]).then(() => {
          showNotification("Image copiée avec succès dans le presse papier", "success");
        });
      })
      .catch((err) => {
        log.error("Erreur lors de la copie :", err);
      });
  };

  const handleDownloadPNG = () => {
    const node = document.getElementById("bullseye-wrapper");
    if (!node) return;

    const scale = 2; // Render larger, then shrink for better quality
    const style = window.getComputedStyle(node);
    const width = parseInt(style.getPropertyValue('width'), 10);
    const height = parseInt(style.getPropertyValue('height'), 10);

    domtoimage
      .toPng(node, {
        width: width * scale,
        height: height * scale,
        style: {
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        },
      })
      .then((dataUrl) => {
        const link = document.createElement('a');
        link.download = `bullseye_${new Date().toISOString().slice(0, 10)}.png`;
        link.href = dataUrl;
        link.click();
      })
      .catch((err) => {
        log.error('dom-to-image =>', err);
      });
  };

  const summaryText = useMemo(() => {
    // Merge provided data with a full map of Normal segments so first open is considered "Normal"
    const mergedData = {
      ...createAllNormalSegments(),
      ...(structuredData || {})
    };

    // Count segments by anomaly type
    const segmentsByAnomaly = {};
    const totalSegments = Object.keys(mergedData).length;

    Object.entries(mergedData).forEach(([segmentId, anomaly]) => {
      if (!segmentsByAnomaly[anomaly]) {
        segmentsByAnomaly[anomaly] = [];
      }
      segmentsByAnomaly[anomaly].push(segmentId);
    });

    // If all segments are normal
    if ((segmentsByAnomaly["Normal"]?.length || 0) === totalSegments) {
      return "Cinétique segmentaire normale";
    }

    // Check if all segments have the same anomaly (excluding normal)
    const anomalies = Object.entries(segmentsByAnomaly)
      .filter(([anomaly]) => anomaly !== "Normal");

    if (anomalies.length === 1 && anomalies[0][1].length === totalSegments) {
      // All segments have the same anomaly
      return `${anomalies[0][0]} globale`;
    }

    // Find the most common anomaly
    let mostCommonAnomaly = null;
    let maxCount = 0;

    anomalies.forEach(([anomaly, segments]) => {
      if (segments.length > maxCount) {
        mostCommonAnomaly = anomaly;
        maxCount = segments.length;
      }
    });

    // Build the summary text
    let text = "";
    const sortedAnomalies = anomalies.sort(([, a], [, b]) => b.length - a.length);

    // If no normal segments and one anomaly represents >50% of segments
    if (!segmentsByAnomaly["Normal"] && mostCommonAnomaly && maxCount > totalSegments / 2) {
      // List minority anomalies first
      sortedAnomalies.forEach(([anomaly, segments]) => {
        if (anomaly !== mostCommonAnomaly) {
          const segmentsText = segments
            .map((seg) => formatSegmentName(seg))
            .join(", ");
          text += `${anomaly} ${segmentsText}. `;
        }
      });

      // Then mention the majority
      text += `Le reste des segments présente une ${mostCommonAnomaly.toLowerCase()}.`;
    } else {
      // Standard listing for all anomalies
      sortedAnomalies.forEach(([anomaly, segments]) => {
        const segmentsText = segments
          .map((seg) => formatSegmentName(seg))
          .join(", ");
        text += `${anomaly} ${segmentsText}. `;
      });
    }

    return text.trim();
  }, [structuredData]);

  if (!inline && !visible) return null;

  return (
    <div
      ref={popperRef}
      style={inline ? {
        background: "#fff",
        border: "1px solid #d1d5db",
        borderRadius: 8,
        padding: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        width: "100%",
        overflow: "auto"
      } : {
        ...styles.popper,
        zIndex: 9999,
        background: "#fff",
        border: "1px solid #d1d5db",
        borderRadius: 8,
        padding: "12px",
        boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
        maxWidth: "90vw",
        maxHeight: "90vh",
        overflow: "auto",
      }}
      {...(!inline ? attributes.popper : {})}
      onClick={inline ? undefined : (e) => e.stopPropagation()}
    >
      {notification && (
        <Notification
          key={notification.id}
          id={notification.id}
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
        />
      )}
      {/* Header */}
      <div
        style={{
          marginBottom: 12,
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", gap: "8px" }}>
          {!inline && (
            <Button
              onClick={onClose}
              variant="secondary"
              icon={FaTimes}
            >
              Fermer
            </Button>
          )}
        </div>
      </div>

      {/* Main content */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <div id="bullseye-wrapper" style={{ flex: "0 0 auto" }}>
          <BullseyeDiagram
            className="bullseye-diagram"
            value={structuredData}
            onChange={onBullseyeChange}
            selectedAnomaly={selectedAnomaly}
            onSelectedAnomalyChange={setSelectedAnomaly}
            width={280}
            height={280}
          />
        </div>
        
        <div style={{ flex: "1",  maxWidth: 400 }}>
          {/* Anomaly selection */}
          <div style={{ marginBottom: 12 }}>
            <h4 style={{ margin: "0 0 6px 0", fontSize: "13px", fontWeight: "500", color: "#374151" }}>
              Sélectionner une anomalie
            </h4>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
              {ANOMALIES.map((anom) => (
                <button
                  key={anom}
                  onClick={() => setSelectedAnomaly(anom)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 8px",
                    cursor: "pointer",
                    border: selectedAnomaly === anom ? "2px solid #3b82f6" : "1px solid #e5e7eb",
                    background: selectedAnomaly === anom ? "#eff6ff" : "#fff",
                    borderRadius: 4,
                    fontSize: 14,
                    transition: "all 0.2s",
                  }}
                >
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      backgroundColor: getColorFromAnomaly(anom),
                      border: "1px solid #d1d5db",
                      borderRadius: 2,
                    }}
                  />
                  {anom}
                </button>
              ))}
            </div>
          </div>

                     {/* Quick actions */}
           <div>
             <h4 style={{ margin: "0 0 6px 0", fontSize: "13px", fontWeight: "500", color: "#374151" }}>
               Transfromer tout en
             </h4>
             <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
               <button style={compactActionStyle} onClick={() => applyToAll("Normal")}>
                 Normal
               </button>
               <button style={compactActionStyle} onClick={() => applyToAll("Hyperkinésie")}>
                 Hyperkinésie
               </button>
               <button style={compactActionStyle} onClick={() => applyToAll("Hypokinésie")}>
                 Hypokinésie
               </button>
               <button style={compactActionStyle} onClick={() => applyToAll("Akinésie")}>
                 Akinésie
               </button>
               <button style={compactActionStyle} onClick={() => applyToAll("Dyskinésie")}>
                 Dyskinésie
               </button>
               <button style={compactActionStyle} onClick={() => applyToAll("Anévrysme")}>
                 Anévrysme
               </button>
             </div>
           </div>
        </div>
      </div>

      {/* Summary */}
      <div style={{ 
        marginTop: 12, 
        padding: "8px 12px", 
        background: "#f9fafb", 
        borderRadius: 6,
        maxWidth: 650,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 12
      }}>
        <div style={{
          fontStyle: "italic",
          fontSize: "12px",
          color: "#4b5563",
          flex: 1
        }}>
          {summaryText}
        </div>
        <Button
          type="inversed"
          variant="secondary"
          onClick={() => navigator.clipboard.writeText(summaryText)}
          style={{ 
            padding: "4px 8px",   
            fontSize: "10px",
            minWidth: "auto",
            flexShrink: 0
          }}
                 >
           Copier
         </Button>
      </div>
    </div>
  );
}
const quickActionStyle = {
  padding: "8px 12px",
  border: "1px solid #e5e7eb",
  background: "#f9fafb",
  textAlign: "left",
  cursor: "pointer",
  fontSize: "13px",
  borderRadius: 6,
  transition: "all 0.2s",
  "&:hover": {
    background: "#f3f4f6",
  }
};

const compactActionStyle = {
  padding: "4px 8px",
  border: "1px solid #e5e7eb",
  background: "#f9fafb",
  textAlign: "center",
  cursor: "pointer",
  fontSize: "13px",
  borderRadius: 4,
  transition: "all 0.2s",
  whiteSpace: "nowrap",
  "&:hover": {
    background: "#f3f4f6",
  }
};

/* ------------------------------------------------------------------
   PopoverBullseye Component
------------------------------------------------------------------ */

function PopoverBullseye({ btnRef, structuredData, onBullseyeChange }) {
  const [showBullseye, setShowBullseye] = useState(false);

  const toggleBullseye = () => {
    setShowBullseye(prev => !prev);
  };

  return (
    !isCapacitor ? null :
    <div>
      <Button icon={FaChartLine} variant="orange" ref={btnRef} onClick={toggleBullseye}>
        Schéma cinétique
      </Button>

      <EchoReportView
        anchorEl={btnRef.current}
        visible={showBullseye}
        onClose={() => setShowBullseye(false)}
        structuredData={structuredData}
        onBullseyeChange={onBullseyeChange}
      />
    </div>
  );
}

// Export all necessary components and constants
export default PopoverBullseye;
export { BullseyeDiagram, EchoReportView, ANOMALIES, getColorFromAnomaly, SEGMENT_IDS };

// Programmatic renderer to generate a PNG data URL for a Bullseye image from structured data
export async function renderBullseyeImage(structuredData, options = {}) {
  const width = options.width || 280;
  const scale = options.scale || 2;
  const outputDownscaleFactor = typeof options.outputDownscaleFactor === 'number'
    ? options.outputDownscaleFactor
    : 1;

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-10000px';
  container.style.top = '-10000px';
  container.style.width = `${width}px`;
  container.style.zIndex = '-1';
  document.body.appendChild(container);

  let root;
  try {
    root = createRoot(container);
    root.render(
      React.createElement('div', { id: 'bullseye-wrapper', style: { width: `${width}px` } },
        React.createElement(BullseyeDiagram, { value: structuredData || {}, width, height: options.height || 280 })
      )
    );

    // Wait for render flush
    await new Promise((resolve) => setTimeout(resolve, 50));

    const node = container.querySelector('#bullseye-wrapper');
    if (!node) {
      throw new Error('Bullseye node not found for rendering');
    }

    // Capture full rendered dimensions (including legend) instead of fixed height
    const exportWidth = Math.max(node.scrollWidth || 0, node.offsetWidth || width);
    const exportHeight = Math.max(node.scrollHeight || 0, node.offsetHeight || (options.height || 280));

    const dataUrl = await domtoimage.toPng(node, {
      width: exportWidth * scale,
      height: exportHeight * scale,
      style: {
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        backgroundColor: '#ffffff'
      },
      quality: 1,
      bgcolor: '#ffffff',
    });

    // Optionally downscale the final PNG before returning
    if (outputDownscaleFactor > 0 && outputDownscaleFactor < 1) {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = dataUrl;
      });

      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.floor(img.width * outputDownscaleFactor));
      canvas.height = Math.max(1, Math.floor(img.height * outputDownscaleFactor));
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    }

    return dataUrl;
  } finally {
    try {
      if (root) {
        root.unmount();
      }
    } catch (_) {}
    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }
  }
}